import Cookies from 'js-cookie';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

export const getAuthHeaders = (contentType = 'application/json') => {
  const token = Cookies.get('token') || localStorage.getItem('token');
  const headers = {};
  
  if (contentType && contentType !== 'multipart/form-data') {
    headers['Content-Type'] = contentType;
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

export const apiFetch = async (url, options = {}) => {
  const { headers, ...restOptions } = options;
  
  // Determine content type - if body is FormData, don't set Content-Type header
  const isFormData = options.body instanceof FormData;
  const contentType = isFormData ? 'multipart/form-data' : 'application/json';
  
  const authHeaders = getAuthHeaders(contentType);
  
  const response = await fetch(url, {
    ...restOptions,
    headers: {
      ...authHeaders,
      ...headers,
    },
  });
  
  return response;
};

export const AdminService = {
  async getServiceVendors(vendorTypeId) {
    const response = await apiFetch(`${API_BASE_URL}/admin/service-vendors/${vendorTypeId}`, { method: 'GET' });
    return response.json();
  },

  async createServiceVendor(payload) {
    const response = await apiFetch(`${API_BASE_URL}/admin/service-vendors`, {
      method: 'POST', body: JSON.stringify(payload),
    });
    return response.json();
  },

  // Set vendor status ('A' = Active / Approved, 'D' = Deactivated)
  async setVendorStatus(vendorId, status) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/setVendorStatus/${vendorId}`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error setting vendor status: ${error.message}`);
    }
  },

  // Reset vendor password
  async resetVendorPassword(vendorId, newPassword) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/resetVendorPassword/${vendorId}`, {
        method: 'PUT',
        body: JSON.stringify({ newPassword }),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error resetting password: ${error.message}`);
    }
  },

  // Speciality CRUD
  async getAllSpecialities() {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/speciality`, {
        method: 'GET',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error fetching specialities: ${error.message}`);
    }
  },

  async getSpecialityById(id) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/speciality/${id}`, {
        method: 'GET',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error fetching speciality: ${error.message}`);
    }
  },

  async addSpeciality(data) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/speciality`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error adding speciality: ${error.message}`);
    }
  },

  async updateSpeciality(id, data) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/speciality/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error updating speciality: ${error.message}`);
    }
  },

  async deleteSpeciality(id) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/speciality/${id}`, {
        method: 'DELETE',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error deleting speciality: ${error.message}`);
    }
  },


  // Pharmacy Category CRUD
  async getAllPharmacyCategories() {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/pharmacy-category`, {
        method: 'GET',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error fetching pharmacy categories: ${error.message}`);
    }
  },

  async addPharmacyCategory(data) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/pharmacy-category`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error adding pharmacy category: ${error.message}`);
    }
  },

  async updatePharmacyCategory(id, data) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/pharmacy-category/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error updating pharmacy category: ${error.message}`);
    }
  },

  async deletePharmacyCategory(id) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/pharmacy-category/${id}`, {
        method: 'DELETE',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error deleting pharmacy category: ${error.message}`);
    }
  },

  // LabTest Category CRUD
  async getAllLabTestCategories() {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/labtest-category`, {
        method: 'GET',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error fetching labtest categories: ${error.message}`);
    }
  },

  async addLabTestCategory(data) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/labtest-category`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error adding labtest category: ${error.message}`);
    }
  },

  async updateLabTestCategory(id, data) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/labtest-category/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error updating labtest category: ${error.message}`);
    }
  },

  async deleteLabTestCategory(id) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/labtest-category/${id}`, {
        method: 'DELETE',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error deleting labtest category: ${error.message}`);
    }
  },

  // HomeCare Category CRUD
  async getAllHomeCareCategories() {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/homecare-category`, {
        method: 'GET',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error fetching homecare categories: ${error.message}`);
    }
  },

  async addHomeCareCategory(data) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/homecare-category`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error adding homecare category: ${error.message}`);
    }
  },

  async updateHomeCareCategory(id, data) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/homecare-category/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error updating homecare category: ${error.message}`);
    }
  },

  async deleteHomeCareCategory(id) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/homecare-category/${id}`, {
        method: 'DELETE',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error deleting homecare category: ${error.message}`);
    }
  },

  // ==========================================
  // DIGI LIVING LEADS MANAGEMENT
  // ==========================================
  async getLeads(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.service_type) queryParams.append('service_type', filters.service_type);
      if (filters.lead_status) queryParams.append('lead_status', filters.lead_status);
      if (filters.delivery_mode) queryParams.append('delivery_mode', filters.delivery_mode);
      if (filters.vendor_id) queryParams.append('vendor_id', filters.vendor_id);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.offset) queryParams.append('offset', filters.offset);

      const response = await apiFetch(`${API_BASE_URL}/admin/leads?${queryParams.toString()}`, {
        method: 'GET',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error fetching leads: ${error.message}`);
    }
  },

  async updateLeadStatus(payload) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/leads/update-status`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error updating lead: ${error.message}`);
    }
  },

  async createLead(payload) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/leads/create`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error creating lead: ${error.message}`);
    }
  },

  async getLeadDeliveryLogs(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.lead_id) queryParams.append('lead_id', filters.lead_id);
      if (filters.vendor_id) queryParams.append('vendor_id', filters.vendor_id);
      if (filters.delivery_state) queryParams.append('delivery_state', filters.delivery_state);

      const response = await apiFetch(`${API_BASE_URL}/admin/lead-delivery-logs?${queryParams.toString()}`, {
        method: 'GET',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error fetching delivery logs: ${error.message}`);
    }
  },

  // ==========================================
  // SUBSCRIPTION & VENDOR ACCESS MODELS
  // ==========================================
  async getSubscriptionPlans() {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/subscriptions/plans`, {
        method: 'GET',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error fetching subscription plans: ${error.message}`);
    }
  },

  async saveSubscriptionPlan(payload) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/subscriptions/plans`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error saving plan: ${error.message}`);
    }
  },

  async getVendorAccessModel(vendorId) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/vendors/${vendorId}/access-model`, {
        method: 'GET',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error fetching vendor access model: ${error.message}`);
    }
  },

  async updateVendorAccessModel(vendorId, payload) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/vendors/${vendorId}/access-model`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error updating vendor access model: ${error.message}`);
    }
  },

  async getVendorIntegrations() {
    const response = await apiFetch(`${API_BASE_URL}/admin/vendor-integrations`, { method: 'GET' });
    return response.json();
  },

  async getVendorIntegration(vendorId) {
    const response = await apiFetch(`${API_BASE_URL}/admin/vendors/${vendorId}/integration`, { method: 'GET' });
    return response.json();
  },

  async saveVendorIntegration(vendorId, payload) {
    const response = await apiFetch(`${API_BASE_URL}/admin/vendors/${vendorId}/integration`, {
      method: 'POST', body: JSON.stringify(payload),
    });
    return response.json();
  },

  async generateVendorCredentials(vendorId) {
    const response = await apiFetch(`${API_BASE_URL}/admin/vendors/${vendorId}/integration/credentials`, { method: 'POST' });
    return response.json();
  },

  async testVendorIntegration(vendorId, serviceType) {
    const response = await apiFetch(`${API_BASE_URL}/admin/vendors/${vendorId}/integration/test`, {
      method: 'POST', body: JSON.stringify({ service_type: serviceType }),
    });
    return response.json();
  },

  async getVendorModuleSubscriptions(vendorId = null) {
    try {
      const url = vendorId 
        ? `${API_BASE_URL}/admin/subscriptions/vendor-modules?vendor_id=${vendorId}` 
        : `${API_BASE_URL}/admin/subscriptions/vendor-modules`;
      const response = await apiFetch(url, { method: 'GET' });
      return await response.json();
    } catch (error) {
      throw new Error(`Error fetching module subscriptions: ${error.message}`);
    }
  },

  async saveVendorModuleSubscription(payload) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/subscriptions/vendor-modules`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error saving module subscription: ${error.message}`);
    }
  },

  async deleteSubscriptionPlan(id) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/subscriptions/plans/${id}`, {
        method: 'DELETE',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error deleting plan: ${error.message}`);
    }
  },

  async deleteVendorModuleSubscription(id) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/subscriptions/vendor-modules/${id}`, {
        method: 'DELETE',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error deleting vendor module subscription: ${error.message}`);
    }
  },

  async retryLeadDelivery(id) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/lead-delivery-logs/${id}/retry`, {
        method: 'POST',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error retrying lead delivery: ${error.message}`);
    }
  },

  async getCurrencies() {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/currencies`, { method: 'GET' });
      return await response.json();
    } catch (error) {
      throw new Error(`Error fetching currencies: ${error.message}`);
    }
  },

  async saveCurrency(payload) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/currencies`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error saving currency: ${error.message}`);
    }
  },

  async deleteCurrency(id) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/currencies/${id}`, {
        method: 'DELETE',
      });
      return await response.json();
    } catch (error) {
      throw new Error(`Error deleting currency: ${error.message}`);
    }
  },

  // Location Masters
  async getCountries() {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/locations/countries`);
      return await res.json();
    } catch (error) {
      throw new Error(`Error fetching countries: ${error.message}`);
    }
  },

  async saveCountry(payload) {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/locations/countries`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return await res.json();
    } catch (error) {
      throw new Error(`Error saving country: ${error.message}`);
    }
  },

  async deleteCountry(id) {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/locations/countries/${id}`, { method: 'DELETE' });
      return await res.json();
    } catch (error) {
      throw new Error(`Error deleting country: ${error.message}`);
    }
  },

  async getRegions(countryId = null) {
    try {
      const url = countryId ? `${API_BASE_URL}/admin/locations/regions?country_id=${countryId}` : `${API_BASE_URL}/admin/locations/regions`;
      const res = await apiFetch(url);
      return await res.json();
    } catch (error) {
      throw new Error(`Error fetching states/regions: ${error.message}`);
    }
  },

  async saveRegion(payload) {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/locations/regions`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return await res.json();
    } catch (error) {
      throw new Error(`Error saving state/region: ${error.message}`);
    }
  },

  async deleteRegion(id) {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/locations/regions/${id}`, { method: 'DELETE' });
      return await res.json();
    } catch (error) {
      throw new Error(`Error deleting state/region: ${error.message}`);
    }
  },

  async getCities(regionId = null) {
    try {
      const url = regionId ? `${API_BASE_URL}/admin/locations/cities?region_id=${regionId}` : `${API_BASE_URL}/admin/locations/cities`;
      const res = await apiFetch(url);
      return await res.json();
    } catch (error) {
      throw new Error(`Error fetching cities/areas: ${error.message}`);
    }
  },

  async saveCity(payload) {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/locations/cities`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return await res.json();
    } catch (error) {
      throw new Error(`Error saving city/area: ${error.message}`);
    }
  },

  async deleteCity(id) {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/locations/cities/${id}`, { method: 'DELETE' });
      return await res.json();
    } catch (error) {
      throw new Error(`Error deleting city/area: ${error.message}`);
    }
  },
};


